import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Autocomplete, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, IconButton, Alert, CircularProgress, MenuItem, Tabs, Tab, Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import BusinessIcon from '@mui/icons-material/Business';
import axios from 'axios';

const API = '/api/rrhh';
const fmt = v => Number(v || 0).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const hoy = () => new Date().toLocaleDateString('en-CA');
const periodos = () => {
  const list = [];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    list.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  }
  return list;
};

function TabPanel({ children, value, index }) {
  return value === index ? <Box sx={{ pt: 2 }}>{children}</Box> : null;
}

export default function RxhRRHH() {
  const [empresas, setEmpresas] = useState([]);
  const [empresa, setEmpresa] = useState(null);
  const [tab, setTab] = useState(0);
  const [error, setError] = useState('');

  // Prestadores
  const [prestadores, setPrestadores] = useState([]);
  const [prestLoading, setPrestLoading] = useState(false);
  const [prestOpen, setPrestOpen] = useState(false);
  const [prestForm, setPrestForm] = useState({ tipo_doc: 'DNI', num_doc: '', apellido_paterno: '', apellido_materno: '', nombres: '', fecha_ingreso: hoy() });
  const [prestSaving, setPrestSaving] = useState(false);

  // Recibos
  const [periodo, setPeriodo] = useState(periodos()[0]);
  const [recibos, setRecibos] = useState([]);
  const [reciboLoading, setReciboLoading] = useState(false);
  const [reciboOpen, setReciboOpen] = useState(false);
  const [reciboForm, setReciboForm] = useState({ prestador: null, serie: 'E001', numero: '', monto: '', fecha_emision: hoy(), fecha_pago: hoy(), retencion_4ta: false });
  const [reciboSaving, setReciboSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/empresas`).then(r => setEmpresas(r.data)).catch(() => {});
  }, []);

  const cargarPrestadores = useCallback(() => {
    if (!empresa) return;
    setPrestLoading(true); setError('');
    axios.get(`${API}/trabajadores`, { params: { empresa_id: empresa.empresa_id, tipo_trabajador: 'RXH' } })
      .then(r => setPrestadores(Array.isArray(r.data) ? r.data : []))
      .catch(e => setError(e.response?.data?.error || 'Error al cargar prestadores'))
      .finally(() => setPrestLoading(false));
  }, [empresa]);

  const cargarRecibos = useCallback(() => {
    if (!empresa) return;
    setReciboLoading(true); setError('');
    axios.get(`${API}/rxh/recibos`, { params: { empresa_id: empresa.empresa_id, periodo } })
      .then(r => setRecibos(Array.isArray(r.data) ? r.data : []))
      .catch(e => setError(e.response?.data?.error || 'Error al cargar recibos'))
      .finally(() => setReciboLoading(false));
  }, [empresa, periodo]);

  useEffect(() => { cargarPrestadores(); }, [cargarPrestadores]);
  useEffect(() => { cargarRecibos(); }, [cargarRecibos]);

  const handleCrearPrestador = async () => {
    if (!prestForm.num_doc || !prestForm.apellido_paterno || !prestForm.nombres) return;
    setPrestSaving(true);
    try {
      await axios.post(`${API}/trabajadores`, {
        ...prestForm, empresa_id: empresa.empresa_id, tipo_trabajador: 'RXH', tipo_contrato: 'LOCACION',
        sexo: '', estado_civil: '', direccion: '', telefono: '', email: '',
        cuspp: null, banco_pago: null, banco_cts: null, num_cuenta_cts: null,
      });
      setPrestOpen(false);
      setPrestForm({ tipo_doc: 'DNI', num_doc: '', apellido_paterno: '', apellido_materno: '', nombres: '', fecha_ingreso: hoy() });
      cargarPrestadores();
    } catch (e) { setError(e.response?.data?.error || 'Error al registrar prestador'); }
    finally { setPrestSaving(false); }
  };

  const handleCrearRecibo = async () => {
    if (!reciboForm.prestador || !reciboForm.numero || !reciboForm.monto) return;
    setReciboSaving(true);
    try {
      await axios.post(`${API}/rxh/recibos`, {
        empresa_id: empresa.empresa_id, prestador_id: reciboForm.prestador.id, periodo,
        serie: reciboForm.serie, numero: reciboForm.numero, monto: reciboForm.monto,
        fecha_emision: reciboForm.fecha_emision, fecha_pago: reciboForm.fecha_pago,
        retencion_4ta: reciboForm.retencion_4ta,
      });
      setReciboOpen(false);
      setReciboForm({ prestador: null, serie: 'E001', numero: '', monto: '', fecha_emision: hoy(), fecha_pago: hoy(), retencion_4ta: false });
      cargarRecibos();
    } catch (e) { setError(e.response?.data?.error || 'Error al registrar recibo'); }
    finally { setReciboSaving(false); }
  };

  const handleEliminarRecibo = async (id) => {
    if (!window.confirm('¿Eliminar este recibo?')) return;
    try { await axios.delete(`${API}/rxh/recibos/${id}`); cargarRecibos(); }
    catch (e) { setError(e.response?.data?.error || 'Error al eliminar'); }
  };

  const descargarTxt = async (sub) => {
    setError('');
    try {
      const r = await axios.get(`${API}/rxh/reporte-txt`, { params: { empresa_id: empresa.empresa_id, periodo, sub }, responseType: 'blob' });
      const cd = r.headers['content-disposition'] || '';
      const match = cd.match(/filename="?([^"]+)"?/);
      const fname = match ? match[1] : `rxh.${sub || '4ta'}`;
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a'); a.href = url; a.download = fname; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      if (e.response?.data instanceof Blob) {
        try { setError(JSON.parse(await e.response.data.text()).error || 'Error al generar el archivo'); }
        catch { setError('Error al generar el archivo'); }
      } else setError(e.response?.data?.error || 'Error al generar el archivo');
    }
  };

  const totalPeriodo = recibos.reduce((s, r) => s + Number(r.monto || 0), 0);

  return (
    <Box p={2}>
      <Typography variant="h5" fontWeight="bold" color="purple" gutterBottom>
        Recibos por Honorarios (4ta Categoría)
      </Typography>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Autocomplete
          options={empresas} value={empresa} onChange={(_, v) => setEmpresa(v)}
          getOptionLabel={o => o.razon_social || ''}
          sx={{ minWidth: '16em', maxWidth: 400 }}
          renderInput={p => <TextField {...p} label="Empresa" size="small"
            InputProps={{ ...p.InputProps, startAdornment: <BusinessIcon fontSize="small" sx={{ mr: 0.5, color: 'purple' }} /> }} />}
        />
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Paper sx={{ borderRadius: 2 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: '1px solid #eee', px: 2, pt: 1 }}>
          <Tab label="Prestadores" />
          <Tab label="Recibos del período" />
        </Tabs>

        {/* TAB 0: PRESTADORES */}
        <TabPanel value={tab} index={0}>
          <Box sx={{ px: 2, pb: 2 }}>
            <Box display="flex" justifyContent="flex-end" mb={1}>
              <Button variant="contained" startIcon={<AddIcon />} sx={{ bgcolor: 'purple' }}
                onClick={() => setPrestOpen(true)} disabled={!empresa}>
                Nuevo Prestador
              </Button>
            </Box>
            <TableContainer>
              {prestLoading ? <Box p={3} textAlign="center"><CircularProgress color="secondary" /></Box> : (
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'purple' }}>
                      {['Doc.', 'Apellidos y Nombres', 'Fecha Registro'].map(h => (
                        <TableCell key={h} sx={{ color: 'white', fontWeight: 'bold', fontSize: 12 }}>{h}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {prestadores.length === 0 ? (
                      <TableRow><TableCell colSpan={3} align="center">No hay prestadores registrados</TableCell></TableRow>
                    ) : prestadores.map(p => (
                      <TableRow key={p.id} hover>
                        <TableCell sx={{ fontSize: 12, fontFamily: 'monospace' }}>{p.tipo_doc} {p.num_doc}</TableCell>
                        <TableCell sx={{ fontSize: 12 }}>{p.apellido_paterno} {p.apellido_materno}, {p.nombres}</TableCell>
                        <TableCell sx={{ fontSize: 12 }}>{p.fecha_ingreso?.substring(0, 10)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </TableContainer>
          </Box>
        </TabPanel>

        {/* TAB 1: RECIBOS */}
        <TabPanel value={tab} index={1}>
          <Box sx={{ px: 2, pb: 2 }}>
            <Box display="flex" gap={2} flexWrap="wrap" alignItems="center" mb={2}>
              <TextField select label="Período" size="small" value={periodo}
                onChange={e => setPeriodo(e.target.value)} sx={{ minWidth: '10em' }}>
                {periodos().map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
              </TextField>
              <Button variant="contained" startIcon={<AddIcon />} sx={{ bgcolor: 'purple' }}
                onClick={() => setReciboOpen(true)} disabled={!empresa}>
                Nuevo Recibo
              </Button>
              <Box sx={{ flexGrow: 1 }} />
              <Tooltip title="Archivo .4ta (detalle de comprobantes, para importar en PLAME)">
                <span>
                  <Button variant="outlined" color="secondary" startIcon={<DownloadIcon />}
                    onClick={() => descargarTxt('4ta')} disabled={recibos.length === 0}>
                    Descargar .4ta
                  </Button>
                </span>
              </Tooltip>
              <Tooltip title="Archivo .ps4 (registro de nombres del prestador)">
                <span>
                  <Button variant="outlined" color="secondary" startIcon={<DownloadIcon />}
                    onClick={() => descargarTxt('ps4')} disabled={recibos.length === 0}>
                    Descargar .ps4
                  </Button>
                </span>
              </Tooltip>
            </Box>

            {empresa && (
              <Paper sx={{ p: 2, mb: 2, bgcolor: '#f3e5f5', border: '1px solid purple' }}>
                <Typography variant="subtitle2" color="purple">
                  Total del período: <strong>S/ {fmt(totalPeriodo)}</strong>
                  <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                    ({recibos.length} recibo(s))
                  </Typography>
                </Typography>
              </Paper>
            )}

            <TableContainer>
              {reciboLoading ? <Box p={3} textAlign="center"><CircularProgress color="secondary" /></Box> : (
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'purple' }}>
                      {['Prestador', 'Doc.', 'Serie-Número', 'Monto', 'F. Emisión', 'F. Pago', 'Ret. 4ta', 'Acciones'].map(h => (
                        <TableCell key={h} sx={{ color: 'white', fontWeight: 'bold', fontSize: 12 }}>{h}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {recibos.length === 0 ? (
                      <TableRow><TableCell colSpan={8} align="center">No hay recibos en este período</TableCell></TableRow>
                    ) : recibos.map(r => (
                      <TableRow key={r.id} hover>
                        <TableCell sx={{ fontSize: 12 }}>{r.prestador_nombre}</TableCell>
                        <TableCell sx={{ fontSize: 12, fontFamily: 'monospace' }}>{r.num_doc}</TableCell>
                        <TableCell sx={{ fontSize: 12, fontFamily: 'monospace' }}>{r.serie}-{r.numero}</TableCell>
                        <TableCell sx={{ fontSize: 12, fontFamily: 'monospace' }}>S/ {fmt(r.monto)}</TableCell>
                        <TableCell sx={{ fontSize: 12 }}>{r.fecha_emision?.substring(0, 10)}</TableCell>
                        <TableCell sx={{ fontSize: 12 }}>{r.fecha_pago?.substring(0, 10)}</TableCell>
                        <TableCell sx={{ fontSize: 12 }}>{r.retencion_4ta ? 'Sí' : 'No'}</TableCell>
                        <TableCell>
                          <IconButton size="small" color="error" onClick={() => handleEliminarRecibo(r.id)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </TableContainer>
          </Box>
        </TabPanel>
      </Paper>

      {/* Dialog Nuevo Prestador */}
      <Dialog open={prestOpen} onClose={() => setPrestOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: 'purple', color: 'white' }}>Nuevo Prestador de Servicios</DialogTitle>
        <DialogContent sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField select label="Tipo Documento" size="small" value={prestForm.tipo_doc}
            onChange={e => setPrestForm(f => ({ ...f, tipo_doc: e.target.value }))} sx={{ minWidth: '16em' }}>
            {['DNI', 'CE', 'RUC'].map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
          </TextField>
          <TextField label="Número Documento *" size="small" value={prestForm.num_doc}
            onChange={e => setPrestForm(f => ({ ...f, num_doc: e.target.value }))} sx={{ minWidth: '16em' }} />
          <TextField label="Apellido Paterno *" size="small" value={prestForm.apellido_paterno}
            onChange={e => setPrestForm(f => ({ ...f, apellido_paterno: e.target.value }))} sx={{ minWidth: '16em' }} />
          <TextField label="Apellido Materno" size="small" value={prestForm.apellido_materno}
            onChange={e => setPrestForm(f => ({ ...f, apellido_materno: e.target.value }))} sx={{ minWidth: '16em' }} />
          <TextField label="Nombres *" size="small" value={prestForm.nombres}
            onChange={e => setPrestForm(f => ({ ...f, nombres: e.target.value }))} sx={{ minWidth: '16em' }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPrestOpen(false)}>Cancelar</Button>
          <Button variant="contained" sx={{ bgcolor: 'purple' }} onClick={handleCrearPrestador} disabled={prestSaving}>
            {prestSaving ? <CircularProgress size={18} /> : 'Registrar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Nuevo Recibo */}
      <Dialog open={reciboOpen} onClose={() => setReciboOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: 'purple', color: 'white' }}>Registrar Recibo por Honorarios</DialogTitle>
        <DialogContent sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <Autocomplete
            options={prestadores} value={reciboForm.prestador}
            onChange={(_, v) => setReciboForm(f => ({ ...f, prestador: v }))}
            getOptionLabel={o => `${o.apellido_paterno} ${o.apellido_materno}, ${o.nombres}`}
            sx={{ minWidth: '16em' }}
            renderInput={p => <TextField {...p} label="Prestador *" size="small" />}
          />
          <Box display="flex" gap={2}>
            <TextField label="Serie *" size="small" value={reciboForm.serie}
              onChange={e => setReciboForm(f => ({ ...f, serie: e.target.value }))} sx={{ minWidth: '8em' }} />
            <TextField label="Número *" size="small" value={reciboForm.numero}
              onChange={e => setReciboForm(f => ({ ...f, numero: e.target.value }))} sx={{ minWidth: '8em' }} />
          </Box>
          <TextField label="Monto (S/) *" size="small" type="number" value={reciboForm.monto}
            onChange={e => setReciboForm(f => ({ ...f, monto: e.target.value }))}
            inputProps={{ min: 0, step: '0.01' }} sx={{ minWidth: '16em' }} />
          <Box display="flex" gap={2}>
            <TextField label="Fecha Emisión" size="small" type="date" InputLabelProps={{ shrink: true }}
              value={reciboForm.fecha_emision} onChange={e => setReciboForm(f => ({ ...f, fecha_emision: e.target.value }))} sx={{ minWidth: '12em' }} />
            <TextField label="Fecha Pago" size="small" type="date" InputLabelProps={{ shrink: true }}
              value={reciboForm.fecha_pago} onChange={e => setReciboForm(f => ({ ...f, fecha_pago: e.target.value }))} sx={{ minWidth: '12em' }} />
          </Box>
          <TextField select label="Retención 4ta Categoría" size="small" value={reciboForm.retencion_4ta ? '1' : '0'}
            onChange={e => setReciboForm(f => ({ ...f, retencion_4ta: e.target.value === '1' }))} sx={{ minWidth: '16em' }}>
            <MenuItem value="0">No (monto ≤ S/1,500, o sin retención)</MenuItem>
            <MenuItem value="1">Sí (8% retenido)</MenuItem>
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReciboOpen(false)}>Cancelar</Button>
          <Button variant="contained" sx={{ bgcolor: 'purple' }} onClick={handleCrearRecibo} disabled={reciboSaving}>
            {reciboSaving ? <CircularProgress size={18} /> : 'Registrar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
